import { useMemo, useCallback, useState, useEffect } from 'react'
import {
  DndContext,
  closestCenter,
  pointerWithin,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
  defaultDropAnimationSideEffects,
  type DragEndEvent,
  type DragStartEvent,
  type DragOverEvent,
  type DropAnimation,
  type CollisionDetection,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import type { DataSource } from '@/services/api'
import type { FolderResponse } from '@/hooks/useDatasourceFolders'
import { ConnectionRow } from './ConnectionRow'
import { FolderRow } from './FolderRow'
import { clsx } from 'clsx'
import {
  ServerIcon,
  FolderIcon,
  Bars2Icon,
  LockClosedIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline'

interface DatasourceListProps {
  datasources: DataSource[]
  folders: FolderResponse[]
  onEditDatasource: (datasource: DataSource) => void
  onReorderDatasources: (orderedIds: string[]) => void
  onReorderFolders: (orderedIds: string[]) => void
  onMoveDatasourceToFolder: (datasourceId: string, folderId: string | null) => void
  onCreateSubfolder?: (parentFolderId: string) => void
  searchQuery?: string
  matchingFolderIds?: Set<string>
  matchingDatasourceIds?: Set<string>
}

// Drop animation configuration - matches query library exactly
const dropAnimation: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.5',
      },
    },
  }),
}

// =========================================================================
// CUSTOM COLLISION DETECTION - prioritizes precision with pointer
// =========================================================================
const customCollisionDetection: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args)
  if (pointerCollisions.length > 0) return pointerCollisions
  return closestCenter(args)
}

// =========================================================================
// DRAG OVERLAY COMPONENTS - beautiful overlays shown under cursor during drag
// =========================================================================
// Helper to detect if a Redis connection is in cluster mode
const isRedisClusterConnection = (connection: DataSource): boolean => {
  if (connection.databaseType !== 'Redis') return false
  const params = connection.connectionParams as Record<string, unknown> | undefined
  return Array.isArray(params?.clusterNodes)
}

function ConnectionDragOverlay({ connection, width }: { connection: DataSource; width?: number }) {
  const getDatabaseTypeIcon = () => {
    switch (connection.databaseType) {
      case 'MySQL':
        return <img src="/images/logos/mysql-logo.avif" alt="MySQL" className="w-5 h-5" />
      case 'PostgreSQL':
        return <img src="/images/logos/postgresql-logo.avif" alt="PostgreSQL" className="w-5 h-5" />
      case 'OracleDB':
        return <img src="/images/logos/oracle-logo.png" alt="Oracle" className="w-5 h-5" />
      case 'Redis':
        return <img src="/images/logos/redis-logo.svg" alt="Redis" className="w-5 h-5" />
      case 'Memcached':
        return <img src="/images/logos/memcached-logo.svg" alt="Memcached" className="w-5 h-5" />
      case 'MongoDB':
        return <img src="/images/logos/mongodb-logo.svg" alt="MongoDB" className="w-5 h-5" />
      case 'ClickHouse':
        return <img src="/images/logos/clickhouse-logo.svg" alt="ClickHouse" className="w-5 h-5" />
      case 'ScyllaDB':
        return <img src="/images/logos/scylladb-logo.webp" alt="ScyllaDB" className="w-5 h-5" />
      case 'MSSQL':
      case 'Cassandra':
      case 'SQLite':
        return <ServerIcon className="w-5 h-5 text-gray-500" />
      case 'Milvus':
        return <img src="/images/logos/milvus-logo.svg" alt="Milvus" className="w-5 h-5" />
    }
  }

  return (
    <div
      className="flex items-center gap-3 px-4 py-1.5 bg-white dark:bg-gray-800 rounded-xl cursor-grabbing border border-blue-300/60 dark:border-blue-500/40 shadow-[0_4px_24px_-4px_rgba(59,130,246,0.25),0_8px_32px_-8px_rgba(0,0,0,0.12)] dark:shadow-[0_4px_24px_-4px_rgba(59,130,246,0.2),0_8px_32px_-8px_rgba(0,0,0,0.4)]"
      style={width ? { width, transform: 'scale(1.015)' } : { transform: 'scale(1.015)' }}
    >
      {/* Drag Handle Indicator */}
      <div className="flex-shrink-0 text-blue-400 dark:text-blue-500">
        <Bars2Icon className="w-5 h-5" />
      </div>

      {/* Database Type Icon */}
      <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
        {getDatabaseTypeIcon()}
      </div>

      {/* Connection Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {connection.displayName}
          </h3>
          {connection.isLocked && (
            <LockClosedIcon className="w-4 h-4 text-amber-500 flex-shrink-0" title="Locked for safety" />
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <span className="truncate">
            {connection.host}:{connection.port}
          </span>
          <span className="text-gray-300 dark:text-gray-600">&bull;</span>
          <span className="inline-flex items-center gap-1">
            <span>{connection.databaseType}</span>
            {isRedisClusterConnection(connection) && (
              <span className="inline-flex items-center gap-0.5 px-1 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 rounded">
                <ServerStackIcon className="w-2.5 h-2.5" />
                Cluster
              </span>
            )}
          </span>
          <span className="text-gray-300 dark:text-gray-600">&bull;</span>
          <span>{connection.username}</span>
          {connection.defaultDatabase && (
            <>
              <span className="text-gray-300 dark:text-gray-600">&bull;</span>
              <span className="truncate">{connection.defaultDatabase}</span>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function FolderDragOverlay({ folder, width }: { folder: FolderResponse; width?: number }) {
  const count = folder.datasourceCount ?? 0
  return (
    <div
      className="flex items-center gap-3 px-4 py-2.5 bg-white dark:bg-gray-800 rounded-xl cursor-grabbing border border-amber-300/60 dark:border-amber-500/40 shadow-[0_4px_24px_-4px_rgba(245,158,11,0.25),0_8px_32px_-8px_rgba(0,0,0,0.12)] dark:shadow-[0_4px_24px_-4px_rgba(245,158,11,0.2),0_8px_32px_-8px_rgba(0,0,0,0.4)]"
      style={width ? { width, transform: 'scale(1.015)' } : { transform: 'scale(1.015)' }}
    >
      {/* Drag Handle Indicator */}
      <div className="flex-shrink-0 text-amber-400 dark:text-amber-500">
        <Bars2Icon className="w-5 h-5" />
      </div>

      <FolderIcon className="w-5 h-5 text-amber-500 dark:text-amber-400 flex-shrink-0" />
      <span className="text-sm font-medium text-gray-900 dark:text-white truncate flex-1">
        {folder.name}
      </span>
      {count > 0 && (
        <span className="ml-auto text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full flex-shrink-0 font-medium">
          {count}
        </span>
      )}
    </div>
  )
}

// =========================================================================
// MOVE TO ROOT DROP ZONE - using @dnd-kit useDroppable
// =========================================================================
function MoveToRootDropZone({
  isDraggingFromFolder,
}: {
  isDraggingFromFolder: boolean
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: 'root-drop-zone',
    data: { type: 'root-zone' as const },
  })

  if (!isDraggingFromFolder) return null

  return (
    <div
      ref={setNodeRef}
      className={clsx(
        'mt-2 p-4 rounded-lg border-2 border-dashed transition-all duration-200',
        isOver
          ? 'bg-green-50 dark:bg-green-900/20 border-green-500 ring-2 ring-green-500 ring-offset-2'
          : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50'
      )}
    >
      <div className={clsx(
        'text-center text-sm font-medium',
        isOver
          ? 'text-green-600 dark:text-green-400'
          : 'text-gray-500 dark:text-gray-400'
      )}>
        {isOver ? 'Release to move to root level' : 'Drop here to move to root level'}
      </div>
    </div>
  )
}

// =========================================================================
// ACTIVE DRAG ITEM STATE TYPE
// =========================================================================
interface ActiveDragConnection {
  type: 'connection'
  connectionId: string
  folderId: string | null
  connection: DataSource
}

interface ActiveDragFolder {
  type: 'folder'
  folderId: string
  parentFolderId: string | null
  folder: FolderResponse
}

type ActiveDragItem = ActiveDragConnection | ActiveDragFolder

// =========================================================================
// MAIN COMPONENT
// =========================================================================
function DatasourceListContent({
  datasources,
  folders,
  onEditDatasource,
  onReorderDatasources,
  onReorderFolders,
  onMoveDatasourceToFolder,
  onCreateSubfolder,
  searchQuery,
  matchingFolderIds,
  matchingDatasourceIds,
}: DatasourceListProps) {
  const isSearching = Boolean(searchQuery?.trim())

  // Active drag state for DragOverlay and visual feedback
  const [activeDragItem, setActiveDragItem] = useState<ActiveDragItem | null>(null)
  const [overFolderId, setOverFolderId] = useState<string | null>(null)
  const [dragWidth, setDragWidth] = useState<number | undefined>(undefined)

  // Local order overrides - ensures DOM reflects new order synchronously
  // so the drop animation targets the correct position (not the original).
  // This mirrors the query library pattern where Zustand's synchronous set()
  // updates the DOM before the drop animation measures positions.
  const [localRootDsOrder, setLocalRootDsOrder] = useState<string[] | null>(null)
  const [localFolderDsOrders, setLocalFolderDsOrders] = useState<Map<string, string[]>>(new Map())
  const [localFolderOrder, setLocalFolderOrder] = useState<string[] | null>(null)

  // Clear local overrides when external data changes (API response arrived)
  useEffect(() => {
    setLocalRootDsOrder(null)
    setLocalFolderDsOrders(new Map())
  }, [datasources])
  useEffect(() => {
    setLocalFolderOrder(null)
  }, [folders])

  // Sensors: 8px drag distance (prevents accidental drags) + keyboard support
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  // Group datasources by folder
  const { folderDatasources, rootDatasources } = useMemo(() => {
    const folderMap = new Map<string, DataSource[]>()
    const root: DataSource[] = []

    for (const ds of datasources) {
      if (ds.folderId) {
        const existing = folderMap.get(ds.folderId) || []
        existing.push(ds)
        folderMap.set(ds.folderId, existing)
      } else {
        root.push(ds)
      }
    }

    // Sort datasources within each group by order, applying local overrides
    for (const [key, value] of folderMap) {
      const localOrder = localFolderDsOrders.get(key)
      if (localOrder) {
        const orderMap = new Map(localOrder.map((id, i) => [id, i]))
        folderMap.set(key, [...value].sort((a, b) => {
          const aIdx = orderMap.get(a.id || '')
          const bIdx = orderMap.get(b.id || '')
          if (aIdx !== undefined && bIdx !== undefined) return aIdx - bIdx
          if (aIdx !== undefined) return -1
          if (bIdx !== undefined) return 1
          return (a.order ?? 0) - (b.order ?? 0)
        }))
      } else {
        folderMap.set(key, value.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)))
      }
    }

    // Apply local order override for root datasources if available
    let sortedRoot: DataSource[]
    if (localRootDsOrder) {
      const orderMap = new Map(localRootDsOrder.map((id, i) => [id, i]))
      sortedRoot = [...root].sort((a, b) => {
        const aIdx = orderMap.get(a.id || '')
        const bIdx = orderMap.get(b.id || '')
        if (aIdx !== undefined && bIdx !== undefined) return aIdx - bIdx
        if (aIdx !== undefined) return -1
        if (bIdx !== undefined) return 1
        return (a.order ?? 0) - (b.order ?? 0)
      })
    } else {
      sortedRoot = root.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    }

    return {
      folderDatasources: folderMap,
      rootDatasources: sortedRoot,
    }
  }, [datasources, localRootDsOrder, localFolderDsOrders])

  // Build folder hierarchy: group folders by parent and sort by orderIndex
  const { rootFolders, childFoldersByParent } = useMemo(() => {
    const sorted = [...folders].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))

    const roots: FolderResponse[] = []
    const children = new Map<string, FolderResponse[]>()

    for (const folder of sorted) {
      if (!folder.parentFolderId) {
        roots.push(folder)
      } else {
        const siblings = children.get(folder.parentFolderId) || []
        siblings.push(folder)
        children.set(folder.parentFolderId, siblings)
      }
    }

    // Apply local folder order override if available
    let sortedRoots: FolderResponse[]
    if (localFolderOrder) {
      const orderMap = new Map(localFolderOrder.map((id, i) => [id, i]))
      sortedRoots = [...roots].sort((a, b) => {
        const aIdx = orderMap.get(a.id || '')
        const bIdx = orderMap.get(b.id || '')
        if (aIdx !== undefined && bIdx !== undefined) return aIdx - bIdx
        if (aIdx !== undefined) return -1
        if (bIdx !== undefined) return 1
        return (a.orderIndex ?? 0) - (b.orderIndex ?? 0)
      })
    } else {
      sortedRoots = roots
    }

    return { rootFolders: sortedRoots, childFoldersByParent: children }
  }, [folders, localFolderOrder])

  // Filter folders when searching
  const filteredRootFolders = useMemo(() => {
    if (!isSearching) return rootFolders

    const shouldShowFolder = (folder: FolderResponse): boolean => {
      if (!folder.id) return false
      if (matchingFolderIds?.has(folder.id)) return true
      const datasourcesInFolder = folderDatasources.get(folder.id) || []
      if (datasourcesInFolder.some(ds => ds.id && matchingDatasourceIds?.has(ds.id))) return true
      const childFolders = childFoldersByParent.get(folder.id) || []
      return childFolders.some(child => shouldShowFolder(child))
    }

    return rootFolders.filter(folder => shouldShowFolder(folder))
  }, [rootFolders, isSearching, matchingFolderIds, matchingDatasourceIds, folderDatasources, childFoldersByParent])

  // Filter child folders when searching
  const getFilteredChildFolders = useCallback((parentId: string): FolderResponse[] => {
    const children = childFoldersByParent.get(parentId) || []
    if (!isSearching) return children

    return children.filter(folder => {
      if (!folder.id) return false
      if (matchingFolderIds?.has(folder.id)) return true
      const datasourcesInFolder = folderDatasources.get(folder.id) || []
      return datasourcesInFolder.some(ds => ds.id && matchingDatasourceIds?.has(ds.id))
    })
  }, [childFoldersByParent, isSearching, matchingFolderIds, matchingDatasourceIds, folderDatasources])

  // Filter root datasources when searching
  const filteredRootDatasources = useMemo(() => {
    if (!isSearching) return rootDatasources
    return rootDatasources.filter(ds => ds.id && matchingDatasourceIds?.has(ds.id))
  }, [rootDatasources, isSearching, matchingDatasourceIds])

  // Determine which folders should be force-expanded during search
  const forceExpandedFolderIds = useMemo(() => {
    if (!isSearching) return new Set<string>()

    const expanded = new Set<string>()
    for (const folder of folders) {
      if (!folder.id) continue
      const datasourcesInFolder = folderDatasources.get(folder.id) || []
      const hasMatchingDatasource = datasourcesInFolder.some(ds => ds.id && matchingDatasourceIds?.has(ds.id))
      if (matchingFolderIds?.has(folder.id) || hasMatchingDatasource) {
        expanded.add(folder.id)
        if (folder.parentFolderId) {
          expanded.add(folder.parentFolderId)
        }
      }
      const childFolders = childFoldersByParent.get(folder.id) || []
      const hasMatchingChild = childFolders.some(child => {
        if (!child.id) return false
        if (matchingFolderIds?.has(child.id)) return true
        const childDatasources = folderDatasources.get(child.id) || []
        return childDatasources.some(ds => ds.id && matchingDatasourceIds?.has(ds.id))
      })
      if (hasMatchingChild) {
        expanded.add(folder.id)
      }
    }
    return expanded
  }, [isSearching, folders, folderDatasources, matchingFolderIds, matchingDatasourceIds, childFoldersByParent])

  // =========================================================================
  // DND EVENT HANDLERS
  // =========================================================================
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event
    const data = active.data.current as { type: string; connectionId?: string; folderId?: string | null; connection?: DataSource; parentFolderId?: string | null; folder?: FolderResponse } | undefined

    if (!data) return

    // Capture the width of the dragged element so the overlay matches
    const initialRect = active.rect.current.initial
    if (initialRect) {
      setDragWidth(initialRect.width)
    }

    if (data.type === 'connection' && data.connection) {
      setActiveDragItem({
        type: 'connection',
        connectionId: data.connectionId || '',
        folderId: (data.folderId as string) || null,
        connection: data.connection,
      })
    } else if (data.type === 'folder' && data.folder) {
      setActiveDragItem({
        type: 'folder',
        folderId: data.folderId as string || '',
        parentFolderId: (data.parentFolderId as string) || null,
        folder: data.folder,
      })
    }
  }, [])

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { active, over } = event
    if (!over || !active.data.current) {
      setOverFolderId(null)
      return
    }

    const activeData = active.data.current as { type: string } | undefined
    const overData = over.data.current as { type: string; folderId?: string } | undefined

    // Track when a connection is being dragged over a folder for visual feedback
    if (activeData?.type === 'connection' && overData?.type === 'folder') {
      setOverFolderId(overData.folderId || null)
    } else {
      setOverFolderId(null)
    }
  }, [])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event

    setActiveDragItem(null)
    setOverFolderId(null)
    setDragWidth(undefined)

    if (!over || active.id === over.id) return

    const activeData = active.data.current as {
      type: string
      connectionId?: string
      folderId?: string | null
      parentFolderId?: string | null
    } | undefined
    const overData = over.data.current as {
      type: string
      connectionId?: string
      folderId?: string | null
      parentFolderId?: string | null
    } | undefined

    if (!activeData || !overData) return

    // Connection reordering within the same group
    if (activeData.type === 'connection' && overData.type === 'connection') {
      const activeFolderId = activeData.folderId || null
      const overFolderIdVal = overData.folderId || null

      // Only reorder if in the same folder (or both at root)
      if (activeFolderId === overFolderIdVal) {
        const datasourceList = activeFolderId
          ? folderDatasources.get(activeFolderId) || []
          : rootDatasources

        const oldIndex = datasourceList.findIndex(ds => `conn::${ds.id}` === String(active.id))
        const newIndex = datasourceList.findIndex(ds => `conn::${ds.id}` === String(over.id))

        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
          const reordered = arrayMove(datasourceList, oldIndex, newIndex)
          const orderedIds = reordered.map(ds => ds.id).filter((id): id is string => !!id)
          // Update local state first so DOM reflects new order before drop animation
          if (activeFolderId) {
            setLocalFolderDsOrders(prev => new Map([...prev, [activeFolderId, orderedIds]]))
          } else {
            setLocalRootDsOrder(orderedIds)
          }
          onReorderDatasources(orderedIds)
        }
      }
    }

    // Move connection to folder
    if (activeData.type === 'connection' && overData.type === 'folder') {
      const connectionId = activeData.connectionId
      const targetFolderId = overData.folderId
      if (connectionId && targetFolderId) {
        onMoveDatasourceToFolder(connectionId, targetFolderId)
      }
    }

    // Move connection to root
    if (activeData.type === 'connection' && overData.type === 'root-zone') {
      const connectionId = activeData.connectionId
      const activeFolderId = activeData.folderId
      if (connectionId && activeFolderId) {
        onMoveDatasourceToFolder(connectionId, null)
      }
    }

    // Folder reordering within the same parent
    if (activeData.type === 'folder' && overData.type === 'folder') {
      const activeParent = activeData.parentFolderId || null
      const overParent = overData.parentFolderId || null

      if (activeParent === overParent) {
        const folderList = activeParent
          ? childFoldersByParent.get(activeParent) || []
          : rootFolders

        const oldIndex = folderList.findIndex(f => `ds-folder::${f.id}` === String(active.id))
        const newIndex = folderList.findIndex(f => `ds-folder::${f.id}` === String(over.id))

        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
          const reordered = arrayMove(folderList, oldIndex, newIndex)
          const orderedIds = reordered.map(f => f.id).filter((id): id is string => !!id)
          // Update local state first so DOM reflects new order before drop animation
          if (!activeParent) {
            setLocalFolderOrder(orderedIds)
          }
          onReorderFolders(orderedIds)
        }
      }
    }
  }, [rootDatasources, folderDatasources, rootFolders, childFoldersByParent, onReorderDatasources, onReorderFolders, onMoveDatasourceToFolder])

  const handleDragCancel = useCallback(() => {
    setActiveDragItem(null)
    setOverFolderId(null)
    setDragWidth(undefined)
  }, [])

  // =========================================================================
  // SORTABLE ID ARRAYS for SortableContext
  // =========================================================================
  const rootFolderSortableIds = useMemo(() =>
    filteredRootFolders.map(f => `ds-folder::${f.id}`).filter(id => id !== 'ds-folder::undefined'),
    [filteredRootFolders]
  )

  const rootDatasourceSortableIds = useMemo(() =>
    filteredRootDatasources.map(ds => `conn::${ds.id}`).filter(id => id !== 'conn::undefined'),
    [filteredRootDatasources]
  )

  // Create edit handler for a specific datasource
  const createEditHandler = useCallback(
    (datasource: DataSource) => () => onEditDatasource(datasource),
    [onEditDatasource]
  )

  const hasItems = filteredRootFolders.length > 0 || filteredRootDatasources.length > 0

  // Track if we're currently dragging a connection that's in a folder
  const isDraggingConnectionFromFolder = activeDragItem?.type === 'connection' && Boolean(activeDragItem.folderId)

  // Helper function to get all subfolder IDs and their connection IDs (for deletion)
  const getSubfolderInfo = useCallback((folderId: string): { subfolderIds: string[]; subfolderConnectionIds: string[] } => {
    const subfolderIds: string[] = []
    const subfolderConnectionIds: string[] = []

    const childFolders = childFoldersByParent.get(folderId) || []
    for (const child of childFolders) {
      if (child.id) {
        subfolderIds.push(child.id)
        const childDatasources = folderDatasources.get(child.id) || []
        for (const ds of childDatasources) {
          if (ds.id) {
            subfolderConnectionIds.push(ds.id)
          }
        }
      }
    }

    return { subfolderIds, subfolderConnectionIds }
  }, [childFoldersByParent, folderDatasources])

  // Helper function to render a folder and its contents recursively
  const renderFolder = (folder: FolderResponse, depth = 0) => {
    if (!folder.id) return null

    const allDatasourcesInFolder = folderDatasources.get(folder.id) || []
    const datasourcesInFolder = isSearching
      ? allDatasourcesInFolder.filter(ds => ds.id && matchingDatasourceIds?.has(ds.id))
      : allDatasourcesInFolder
    const isForceExpanded = forceExpandedFolderIds.has(folder.id)
    const isFolderMatch = matchingFolderIds?.has(folder.id)
    const connectionIdsInFolder = allDatasourcesInFolder.map(ds => ds.id).filter((id): id is string => !!id)
    const childFolders = getFilteredChildFolders(folder.id)
    const { subfolderIds, subfolderConnectionIds } = getSubfolderInfo(folder.id)

    // Sortable IDs for children
    const childFolderSortableIds = childFolders
      .map(f => `ds-folder::${f.id}`)
      .filter(id => id !== 'ds-folder::undefined')
    const datasourceSortableIds = datasourcesInFolder
      .map(ds => `conn::${ds.id}`)
      .filter(id => id !== 'conn::undefined')

    return (
      <FolderRow
        key={folder.id}
        folder={folder}
        depth={depth}
        onCreateSubfolder={onCreateSubfolder}
        forceExpanded={isForceExpanded}
        isHighlighted={isFolderMatch}
        connectionIds={connectionIdsInFolder}
        subfolderIds={subfolderIds}
        subfolderConnectionIds={subfolderConnectionIds}
        isDropTarget={overFolderId === folder.id}
        disabled={isSearching}
      >
        {/* Render child folders with SortableContext */}
        {childFolders.length > 0 && (
          <SortableContext items={childFolderSortableIds} strategy={verticalListSortingStrategy}>
            {childFolders.map((childFolder) =>
              renderFolder(childFolder, depth + 1)
            )}
          </SortableContext>
        )}
        {/* Render datasources with SortableContext */}
        {datasourcesInFolder.length > 0 && (
          <SortableContext items={datasourceSortableIds} strategy={verticalListSortingStrategy}>
            {datasourcesInFolder.map((datasource) => (
              <ConnectionRow
                key={datasource.id}
                connection={datasource}
                onEdit={createEditHandler(datasource)}
                isHighlighted={Boolean(datasource.id && matchingDatasourceIds?.has(datasource.id))}
                disabled={isSearching}
              />
            ))}
          </SortableContext>
        )}
        {datasourcesInFolder.length === 0 && childFolders.length === 0 && !isSearching && (
          <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 italic">
            No connections in this folder. Drag connections here to organize them.
          </div>
        )}
      </FolderRow>
    )
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={customCollisionDetection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="space-y-1 min-h-[100px]">
        {/* Render root folders with SortableContext */}
        <SortableContext items={rootFolderSortableIds} strategy={verticalListSortingStrategy}>
          {filteredRootFolders.map((folder) => renderFolder(folder, 0))}
        </SortableContext>

        {/* Render root datasources with SortableContext */}
        {filteredRootDatasources.length > 0 && (
          <div className="space-y-1">
            {rootFolders.length > 0 && !isSearching && (
              <div className="px-2 py-1 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Uncategorized
              </div>
            )}
            <SortableContext items={rootDatasourceSortableIds} strategy={verticalListSortingStrategy}>
              {filteredRootDatasources.map((datasource) => (
                <ConnectionRow
                  key={datasource.id}
                  connection={datasource}
                  onEdit={createEditHandler(datasource)}
                  isHighlighted={Boolean(datasource.id && matchingDatasourceIds?.has(datasource.id))}
                  disabled={isSearching}
                />
              ))}
            </SortableContext>
          </div>
        )}

        {/* Empty state */}
        {!hasItems && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <p>{isSearching ? 'No matching connections found' : 'No connections yet'}</p>
          </div>
        )}

        {/* Dedicated drop zone for moving items back to root */}
        <MoveToRootDropZone isDraggingFromFolder={isDraggingConnectionFromFolder} />
      </div>

      {/* Drag Overlay - full-width overlay matching the actual row during drag */}
      <DragOverlay dropAnimation={dropAnimation}>
        {activeDragItem?.type === 'connection' && (
          <ConnectionDragOverlay connection={activeDragItem.connection} width={dragWidth} />
        )}
        {activeDragItem?.type === 'folder' && (
          <FolderDragOverlay folder={activeDragItem.folder} width={dragWidth} />
        )}
      </DragOverlay>
    </DndContext>
  )
}

export function DatasourceList(props: DatasourceListProps) {
  return <DatasourceListContent {...props} />
}
